export type DateRangeKey = "TODAY" | "7" | "30" | "90" | "YEAR" | "CUSTOM" | "ALL";

export interface CandidateDashboardFilter {
  dateRange?: DateRangeKey;
  dateFrom?: string;
  dateTo?: string;
  roles?: string[];
  experienceMin?: number;
  experienceMax?: number;
  locations?: string[];
  workModes?: string[];
  jobTypes?: string[];
  invitationStatus?: string;
  keyword?: string;
  eventTypes?: string[];
}

export interface KpiMetrics {
  views: number;
  interest: number;
  matches: number;
  invitations: number;
  acceptedInvitations: number;
  completion: number;
  visibility: number;
}

export interface ProfileSectionMetric {
  key: string;
  label: string;
  filledCount: number;
  totalCount: number;
  percent: number;
  detail: string;
}

export interface ProfileIntelligence {
  completionPercent: number;
  completedSections: number;
  totalSections: number;
  missingFields: number;
  sections: ProfileSectionMetric[];
}

export interface ProfileQualitySignal {
  id: string;
  label: string;
  present: boolean;
  detail: string | null;
}

export interface ProfileQualitySignals {
  presentCount: number;
  totalSignals: number;
  signals: ProfileQualitySignal[];
}

export interface ImprovementItem {
  id: string;
  section: string;
  action: string;
  reason: string;
  priority: string;
  path: string;
}

export interface ImprovementCenter {
  items: ImprovementItem[];
  itemCount: number;
}

export interface Opportunity {
  jobId: string;
  jobTittle: string | null;
  companyId: string;
  companyName: string | null;
  companyLogoUrl: string | null;
  industry: string | null;
  roleLevel: string | null;
  miniExperience: string | null;
  maxExperience: string | null;
  location: string[] | null;
  workType: string | null;
  noticePeriodDays: number | null;
  compensationOffer: number | null;
  jobStatus: boolean;
  invitationStatus: string | null;
  invitedAt: string | null;
  matchPercent: number;
  matchReasons: string[];
}

export interface OpportunityIntelligence {
  totalMatches: number;
  opportunities: Opportunity[];
}

export interface DiscoveryPoint {
  date: string;
  views: number;
  discoveries: number;
}

export interface RecruiterActivity {
  recruiterUserId: string | null;
  recruiterName: string | null;
  designation: string | null;
  companyName: string | null;
  companyLogoUrl: string | null;
  activityCount: number;
  lastActivityType: string | null;
  lastActivityMessage: string | null;
  lastActiveAt: string | null;
}

export interface ActivityEvent {
  id: string;
  eventType: string | null;
  category: string | null;
  message: string | null;
  actorUserId: string | null;
  actorName: string | null;
  actorRole: string | null;
  entityType: string | null;
  entityId: string | null;
  createdAt: string | null;
}

export interface CareerIntelligence {
  totalYearsOfExperience: number | null;
  currentRole: string | null;
  currentCompany: string | null;
  progressionStage: string | null;
  leadershipYears: number;
  teamSizeLed: number | null;
  notableCompanies: string[] | null;
  domains: string[] | null;
}

export interface LeadershipIntelligence {
  leadershipSignal: boolean;
  teamSizeLed: number | null;
  ownerShipLevels: string[] | null;
  hiringAuthority: string[] | null;
  managementLevelBucket: string | null;
  leadershipScore: number;
  responsibilities: string[];
}

export interface SkillIntelligence {
  skill: string;
  years: number | null;
  category: string;
}

export interface SkillsIntelligence {
  skills: SkillIntelligence[];
  categories: string[];
  totalSkills: number;
}

export interface ImpactIntelligence {
  impactTypes: string[] | null;
  impactDescriptions: string[] | null;
  businessImpactScore: number;
  scaleBucket: string | null;
}

export interface AchievementsIntelligence {
  achievements: string[] | null;
  reputationSignals: string[] | null;
  achievementScore: number;
}

export interface NextStep {
  id: string;
  title: string;
  description: string;
  section: string;
  priority: string;
  path: string;
}

export interface CandidateDashboard {
  greeting: string;
  fullName: string | null;
  headline: string | null;
  profilePhotoUrl: string | null;
  dateRangeApplied: string;
  generatedAtEpochMilli: number;

  kpis: KpiMetrics;
  profileIntelligence: ProfileIntelligence;
  profileQualitySignals: ProfileQualitySignals;
  improvementCenter: ImprovementCenter;
  opportunities: OpportunityIntelligence;
  recruiterActivity: RecruiterActivity[];
  discovery: DiscoveryPoint[];
  activity: ActivityEvent[];
  careerIntelligence: CareerIntelligence;
  leadershipIntelligence: LeadershipIntelligence;
  skillsIntelligence: SkillsIntelligence;
  impactIntelligence: ImpactIntelligence;
  achievementsIntelligence: AchievementsIntelligence;
  nextSteps: NextStep[];
}

export const EMPTY_DASHBOARD: CandidateDashboard = {
  greeting: "Hello",
  fullName: null,
  headline: null,
  profilePhotoUrl: null,
  dateRangeApplied: "30",
  generatedAtEpochMilli: 0,

  kpis: {
    views: 0,
    interest: 0,
    matches: 0,
    invitations: 0,
    acceptedInvitations: 0,
    completion: 0,
    visibility: 0,
  },
  profileIntelligence: {
    completionPercent: 0,
    completedSections: 0,
    totalSections: 0,
    missingFields: 0,
    sections: [],
  },
  profileQualitySignals: {
    presentCount: 0,
    totalSignals: 0,
    signals: [],
  },
  improvementCenter: {
    items: [],
    itemCount: 0,
  },
  opportunities: {
    totalMatches: 0,
    opportunities: [],
  },
  recruiterActivity: [],
  discovery: [],
  activity: [],
  careerIntelligence: {
    totalYearsOfExperience: null,
    currentRole: null,
    currentCompany: null,
    progressionStage: null,
    leadershipYears: 0,
    teamSizeLed: null,
    notableCompanies: [],
    domains: [],
  },
  leadershipIntelligence: {
    leadershipSignal: false,
    teamSizeLed: null,
    ownerShipLevels: [],
    hiringAuthority: [],
    managementLevelBucket: null,
    leadershipScore: 0,
    responsibilities: [],
  },
  skillsIntelligence: {
    skills: [],
    categories: [],
    totalSkills: 0,
  },
  impactIntelligence: {
    impactTypes: [],
    impactDescriptions: [],
    businessImpactScore: 0,
    scaleBucket: null,
  },
  achievementsIntelligence: {
    achievements: [],
    reputationSignals: [],
    achievementScore: 0,
  },
  nextSteps: [],
};

export interface TrackActivityRequest {
  candidateId: string;
  eventType: string;
  message?: string;
  entityType?: string;
  entityId?: string;
}